import { Info } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { CardContainer } from "@/components/card-container";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTaxCompliance } from "@/features/auth";
import { isCardStatusActivationRequired } from "@/features/card/utils/card-status";
import { ManageCard } from "@/features/card/virtual-card";
import { AutoAcceptBanner } from "@/features/payments/components/auto-accept-banner";
import { LatestPayment } from "@/features/payments/components/latest-payment";
import { useGetFirstPagePayments } from "@/features/payments/hooks/use-get-first-page-payments";
import { usePaymentMethods } from "@/features/payments/hooks/use-payment-methods";
import { usePaymentSetupStatus } from "@/features/payments/hooks/use-payment-setup-status";
import { PaymentListComponent } from "@/features/payments/pages/payments/components/payment-list";
import { TaxAlert } from "@/features/tax";
import { AppRoute } from "@/types/routes";

const noop = () => undefined;

export const HomePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { isTaxCompliant } = useTaxCompliance();

  const { payments, isPaymentsLoading, hasPaymentsErrored, refetchPayments } =
    useGetFirstPagePayments();

  const { defaultPaymentMethod, spendCards, cards } = usePaymentMethods();

  const { needsPaymentSetup } = usePaymentSetupStatus();

  const hasPendingStipends = (payments ?? []).some(
    (payment) => payment.type === "stipend"
  );

  useEffect(() => {
    if (needsPaymentSetup) {
      navigate(AppRoute.PAYMENT_METHODS_LIST);
    }
  }, [needsPaymentSetup, navigate]);

  // The payout card comes first, so a participant holding a Virtual Card and a
  // Spend Card sees the Virtual Card. A holder of only study-funded cards still
  // sees one of those.
  const card = defaultPaymentMethod ?? spendCards[0];

  const showRequiresActivation = () => {
    return cards.some((card) => isCardStatusActivationRequired(card.status));
  };

  return (
    <div className="min-h-full pb-12 flex flex-col gap-6">
      {card && (
        <>
          {showRequiresActivation() && (
            <div className="mb-2">
              <Badge variant="outline">
                <Info className="size-4 mr-2" />
                {t("card_activation_required")}
              </Badge>
            </div>
          )}
          <ManageCard card={card} />
        </>
      )}

      <TaxAlert
        isTaxCompliant={isTaxCompliant}
        hasPendingStipends={hasPendingStipends}
      />

      <AutoAcceptBanner />

      <LatestPayment />

      <CardContainer.Header headerClassName="mb-0">
        <CardContainer.HeaderContent>
          <CardContainer.CardTitle> {t("payments")}</CardContainer.CardTitle>
        </CardContainer.HeaderContent>
        {payments && payments.length > 0 && (
          <CardContainer.TopRightContent>
            <Button asChild variant="link" size="sm" className="h-auto p-0">
              <Link to={AppRoute.PAYMENTS}>{t("view_all_payments")}</Link>
            </Button>
          </CardContainer.TopRightContent>
        )}
      </CardContainer.Header>

      <PaymentListComponent
        payments={payments}
        isLoading={isPaymentsLoading}
        isErrored={hasPaymentsErrored}
        hasNextPage={false}
        onFetchMorePayments={noop}
        onRefetchMorePayments={refetchPayments}
        showEndOfHistoryMessage={false}
      />
    </div>
  );
};
